import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquareText, RefreshCw, CheckCircle2, AlertTriangle, MinusCircle, Send, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { API, authHeaders } from "./shared";

const STATUS_META = {
  connected: { label: "Connected", icon: CheckCircle2, cls: "border-green-500/40 text-green-400 bg-green-500/10" },
  needs_attention: { label: "Needs Attention", icon: AlertTriangle, cls: "border-amber-500/40 text-amber-400 bg-amber-500/10" },
  not_configured: { label: "Not Configured", icon: MinusCircle, cls: "border-brand-border text-brand-text-muted bg-brand-surface" },
};

const Badge = ({ status, testId }) => {
  const m = STATUS_META[status] || STATUS_META.not_configured;
  return (
    <span data-testid={testId} className={`font-sans text-[10px] uppercase tracking-wider px-2 py-1 border whitespace-nowrap ${m.cls}`}>
      {m.label}
    </span>
  );
};

export const TwilioStatusChip = () => {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/twilio-status`, authHeaders())
      .then((r) => setStatus(r.data.overall))
      .catch(() => setStatus("not_configured"));
  }, []);
  if (!status) return null;
  const m = STATUS_META[status] || STATUS_META.not_configured;
  const Icon = m.icon;
  return (
    <span data-testid="twilio-status-chip" title="Twilio SMS status — full details in the Go-Live tab"
      className={`inline-flex items-center gap-1.5 font-sans text-[11px] px-2.5 py-1.5 border ${m.cls}`}>
      <Icon size={12} /> Twilio SMS: {m.label}
    </span>
  );
};

const TwilioStatusPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testPhone, setTestPhone] = useState("");
  const [sendingTest, setSendingTest] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/admin/twilio-status`, authHeaders())
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to run Twilio check"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const sendTest = async () => {
    if (!testPhone.trim()) { toast.error("Enter your own phone number first"); return; }
    setSendingTest(true);
    try {
      const r = await axios.post(`${API}/admin/twilio-test-sms`, { phone: testPhone }, authHeaders());
      toast.success(`Test SMS sent to ${r.data.to} (status: ${r.data.status})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test SMS failed");
    } finally { setSendingTest(false); }
  };

  return (
    <div data-testid="twilio-status-panel" className="mt-10">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-serif text-xl text-brand-text flex items-center gap-2">
          <MessageSquareText size={18} className="text-brand-gold" /> Twilio Credentials Check
        </h3>
        <div className="flex items-center gap-3">
          {data && <Badge status={data.overall} testId="twilio-overall-status" />}
          <button onClick={load} disabled={loading} data-testid="twilio-refresh-btn"
            className="flex items-center gap-1.5 font-sans text-[11px] px-3 py-1.5 border border-brand-border text-brand-text-muted hover:border-brand-gold hover:text-brand-gold transition-colors disabled:opacity-50">
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Re-check
          </button>
        </div>
      </div>
      <p className="font-sans text-xs text-brand-text-muted mb-4">
        Read-only check against Twilio — no customer SMS is ever sent. Secrets are always masked.
      </p>

      {loading && !data && <p className="font-sans text-sm text-brand-text-muted">Checking Twilio…</p>}

      {data && (
        <>
          <div className="border border-brand-border divide-y divide-brand-border/50">
            {data.checks.map((c) => (
              <div key={c.key} data-testid={`twilio-check-${c.key}`} className="flex items-start gap-4 p-4 bg-brand-surface/50">
                <div className="flex-1 min-w-0">
                  <p className="font-sans text-sm text-brand-text font-medium">{c.label}</p>
                  <p className="font-sans text-xs text-brand-text-muted mt-0.5">{c.detail}</p>
                </div>
                <Badge status={c.status} testId={`twilio-check-status-${c.key}`} />
              </div>
            ))}
          </div>

          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 font-sans text-[11px] text-brand-text-muted">
            <span data-testid="twilio-masked-sid">Account SID: {data.masked.account_sid || "—"}</span>
            <span data-testid="twilio-masked-token">Auth Token: {data.masked.auth_token || "Missing"}</span>
            <span data-testid="twilio-masked-phone">Number: {data.masked.phone_number || "—"}</span>
          </div>

          {data.fixes.length > 0 && (
            <div data-testid="twilio-fixes" className="mt-5 border border-amber-500/40 bg-amber-500/5 p-4">
              <p className="font-sans text-sm text-amber-400 font-semibold flex items-center gap-2 mb-2">
                <AlertTriangle size={14} /> Fix before SMS goes live
              </p>
              <ol className="list-decimal list-inside space-y-1.5">
                {data.fixes.map((f, i) => (
                  <li key={i} className="font-sans text-xs text-brand-text-muted leading-relaxed">{f}</li>
                ))}
              </ol>
            </div>
          )}

          <div className="mt-5 border border-brand-border p-4 bg-brand-surface/50">
            <p className="font-sans text-sm text-brand-text font-medium mb-1">Send a test SMS to yourself</p>
            <p className="font-sans text-xs text-brand-text-muted mb-3">
              Only goes to the number you type below — never to customers.
            </p>
            <div className="flex gap-2 max-w-md">
              <Input
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="Your phone e.g. 248-555-0123"
                data-testid="twilio-test-phone-input"
                className="bg-brand-bg border-brand-border text-brand-text rounded-none"
              />
              <button onClick={sendTest} disabled={sendingTest} data-testid="twilio-test-send-btn"
                className="flex items-center gap-2 px-4 py-2 bg-brand-gold text-black font-sans text-xs font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 whitespace-nowrap">
                {sendingTest ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                Send Test
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TwilioStatusPanel;
